/* eslint-disable jsx-a11y/no-autofocus */
import { useCallback, useEffect, useState } from 'react';
import type { PropsWithChildren } from 'react';
import type React from 'react';

import { FocusRing, Spinner, Text } from '@change/ds-core-ui';
import { FacebookIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import type { TrackingProperties } from '@change-corgi/tracking-properties';

import type { FacebookLoginResultData } from '#src/app/shared/hooks/auth';
import { useFacebookLoginOrSignup } from '#src/app/shared/hooks/auth';

type Props = {
	onSuccess: (data: FacebookLoginResultData) => void;
	onError: (reason: string) => void;
	loginType: string;
	signupContext?: string;
	disabled?: boolean;
	autoFocus?: boolean;
	onWebview?: () => void;
	optionalOnClickTrackProps?: TrackingProperties;
};

export const FacebookAuthButton: React.FC<PropsWithChildren<Props>> = ({
	disabled,
	autoFocus,
	children,
	loginType,
	signupContext,
	onError,
	onSuccess,
	onWebview,
	optionalOnClickTrackProps,
}) => {
	const { attemptFacebookLogin, result } = useFacebookLoginOrSignup();

	const handleClick = useCallback(() => {
		void attemptFacebookLogin({ fbLoginType: loginType, signupContext, optionalOnClickTrackProps }, onWebview);
	}, [attemptFacebookLogin, loginType, onWebview, optionalOnClickTrackProps, signupContext]);

	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		switch (result.status) {
			case 'success':
				onSuccess(result.data);
				break;
			case 'error':
				onError(result.errorReason);
				break;
			case 'loading':
				// eslint-disable-next-line react-hooks/set-state-in-effect
				setLoading(true);
				break;
			case 'cancelled':
				setLoading(false);
				break;
			default:
				break; // do nothing
		}
	}, [result, onError, onSuccess]);
	return (
		<FocusRing>
			<button
				type="button"
				// border color kept to maintain consistency with the Google button; max-width matches Google's forced max width
				className={
					// eslint-disable-next-line @change-ds/ds-no-raw-color-classname -- Google sign-in button border color
					'text-typography-strong relative m-0 h-10 w-full max-w-[400px] cursor-pointer rounded-sm border border-solid border-[#dadce0] bg-transparent px-0.5 py-0'
				}
				onClick={handleClick}
				disabled={disabled}
				autoFocus={autoFocus}
				data-testid="facebook-auth-button"
			>
				<div className={cn('flex flex-row items-center gap-2 px-2', loading && 'invisible')}>
					<FacebookIcon size={22} aria-hidden />
					<Text as="span" variant="body" size="small" className="flex-1 text-center" data-label>
						{children}
					</Text>
				</div>
				{loading && (
					<div className="absolute top-0 left-0 flex h-full w-full items-center justify-center">
						<Spinner className="size-6" data-testid="facebook-auth-button-spinner" />
						{/* TODO translate label */}
						<span className="sr-only">loading</span>
					</div>
				)}
			</button>
		</FocusRing>
	);
};
